<script setup lang="ts">
const { t, locale } = useAppI18n()
const aboutPageTitle = await getSeoLocaleMessage(locale, '84113')

definePageMeta({
  name: 'about',
})

useHead(() => ({
  title: aboutPageTitle,
  meta: [
    {
      name: 'description',
      content: t('84109'),
    },
  ],
}))

const isChineseLocale = computed(() =>
  String(locale || '')
    .toLowerCase()
    .includes('zh'),
)

const isThreeColumnAdvantagesLocale = computed(() => {
  return ['zh-cn', 'zh-tw', 'zh-hk', 'zh'].includes(String(locale || '').toLowerCase())
})

const introBackdropTitle = computed(() =>
  isChineseLocale.value ? 'INTRODUCTION TO WikiBit' : 'INTRODUCTION TO WikiBit',
)
const advantagesBackdropTitle = computed(() =>
  isChineseLocale.value ? 'ADVANTAGES OF WikiBit' : 'ADVANTAGES OF WikiBit',
)

const heroDescription = computed(() => t('82876'))

const introParagraphs = computed(() => {
  return [t('82878')]
})

const statItems = computed(() => [
  { value: '20,000+', label: t('80337') },
  { value: '60+', label: t('82502') },
  { value: '200+', label: t('82867') },
  { value: '18', label: t('82390') },
  { value: '1,000,000+', label: t('82868') },
])

const advantageCards = computed(() => [
  {
    title: t('82870'),
    description: t('82871'),
    img: 'https://resources1.interface003.com/web30/2026/06/11/47177f1d-9de9-47e2-9209-80c4507b9c02.jpg',
  },
  {
    title: t('82872'),
    description: t('82873'),
    img: 'https://resources1.interface003.com/web30/2026/06/11/2ea0909e-5315-4deb-87d3-a35a0d5d0df7.jpg',
  },
  {
    title: t('82874'),
    description: t('82875'),
    img: 'https://resources1.interface003.com/web30/2026/06/11/a8908943-512d-437b-9fcf-e30bc6e3416d.jpg',
  },
  {
    title: t('82880'),
    description: t('82881'),
    img: 'https://resources1.interface003.com/web30/2026/06/11/2eaea1cc-fc11-4781-ae02-be50fc5e1425.jpg',
  },
  {
    title: t('82882'),
    description: t('82883'),
    img: 'https://resources1.interface003.com/web30/2026/06/11/f6479403-9b3e-4e02-a9d9-5d8ef8e54a75.jpg',
  },
])
</script>

<template>
  <main class="about-page">
    <section class="about-hero">
      <div class="about-hero__overlay"></div>
      <div class="about-shell about-hero__content">
        <h1 class="about-hero__title">About Us</h1>
        <p class="about-hero__description">{{ t('82876') }}</p>
      </div>
    </section>

    <section class="about-intro">
      <div class="about-shell">
        <div class="about-section-heading">
          <p class="about-section-heading__backdrop">{{ introBackdropTitle }}</p>
          <h2 class="about-section-heading__title">{{ t('82877') }}</h2>
          <span class="about-section-heading__line"></span>
        </div>

        <div class="about-intro__body">
          <div class="about-intro__copy">
            <p v-for="paragraph in introParagraphs" :key="paragraph" class="about-intro__paragraph">
              {{ paragraph }}
            </p>
          </div>

          <div class="about-intro__image-wrap">
            <div class="about-intro__image" :aria-label="t('82877')" role="img"></div>
          </div>
        </div>

        <div class="about-stats">
          <article v-for="item in statItems" :key="item.label" class="about-stat-item">
            <p class="about-stat-item__value">{{ item.value }}</p>
            <p class="about-stat-item__label">{{ item.label }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="about-advantages">
      <div class="about-shell">
        <div class="about-section-heading">
          <p class="about-section-heading__backdrop">{{ advantagesBackdropTitle }}</p>
          <h2 class="about-section-heading__title">{{ t('82869') }}</h2>
          <span class="about-section-heading__line"></span>
        </div>

        <div
          class="about-advantages__grid"
          :class="{ 'about-advantages__grid--three-columns': isThreeColumnAdvantagesLocale }"
        >
          <article v-for="card in advantageCards" :key="card.title" class="about-advantage-card">
            <!-- <div class="about-advantage-card__image" :aria-label="card.title" role="img"></div> -->
            <CommonImage class="about-advantage-card__image" :src="card.img" :alt="card.title" />
            <div class="about-advantage-card__body">
              <h3 class="about-advantage-card__title">{{ card.title }}</h3>
              <p class="about-advantage-card__description">{{ card.description }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>
  </main>
</template>

<style lang="less">
.about-page {
  min-height: 100vh;
  background: #fff;
}

.about-shell {
  width: 1200px;
  margin: 0 auto;
}

.about-hero {
  position: relative;
  height: 530px;
  padding-top: 70px;
  background: #fff;
}

.about-hero__overlay {
  position: absolute;
  inset: 0;
  top: 70px;
  background: url('https://resources1.interface003.com/web30/2026/06/11/9453e74e-0676-438c-9560-a87ed8919863.png')
    no-repeat center center;
  background-size: cover;
}

.about-hero__content {
  position: relative;
  z-index: 1;
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  padding-left: 2px;
  color: #fff;
}

.about-hero__title {
  margin: 0;
  font-size: 68px;
  font-weight: 700;
  line-height: 82px;
  letter-spacing: 0;
}

.about-hero__description {
  margin: 18px 0 0;
  max-width: 980px;
  font-size: 16px;
  line-height: 28px;
  color: rgba(255, 255, 255, 0.92);
}

.about-intro {
  padding: 84px 0 108px;
}

.about-section-heading {
  position: relative;
  text-align: center;
}

.about-section-heading__backdrop {
  margin: 0;
  font-size: 66px;
  font-weight: 700;
  line-height: 1;
  color: #f7f7f7;
  text-transform: uppercase;
}

.about-section-heading__title {
  position: relative;
  margin: -4px 0 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
  color: #2f3542;
}

.about-section-heading__line {
  display: block;
  width: 32px;
  height: 4px;
  margin: 14px auto 0;
  border-radius: 999px;
  background: #112d73;
}

.about-intro__body {
  display: grid;
  grid-template-columns: 1fr 520px;
  gap: 66px;
  align-items: center;
  margin-top: 56px;
}

.about-intro__paragraph {
  margin: 0;
  font-size: 16px;
  line-height: 26px;
  color: #4e5969;
}

.about-intro__paragraph + .about-intro__paragraph {
  margin-top: 34px;
}

.about-intro__image {
  width: 100%;
  height: 287px;
  background: url('https://resources1.interface003.com/web30/2026/06/11/dadcb367-f6be-4120-8485-e816b700c258.png')
    no-repeat center center;
}

.about-stats {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-top: 62px;
}

.about-stat-item {
  text-align: center;
}

.about-stat-item__value {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
  color: #2d313d;
}

.about-stat-item__label {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 22px;
  color: #6f7787;
}

.about-advantages {
  padding: 0 0 150px;
}

.about-advantages__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 34px 20px;
  margin-top: 58px;
}

.about-advantages__grid--three-columns {
  grid-template-columns: repeat(3, 1fr);
}

.about-advantage-card {
  border: 1px solid #eef1f5;
  background: #fff;
}

.about-advantages__grid--three-columns .about-advantage-card:nth-child(4) {
  grid-column: 1;
}

.about-advantages__grid--three-columns .about-advantage-card:nth-child(5) {
  grid-column: 2;
}

.about-advantage-card__image {
  width: 100%;
  height: 248px;
  object-fit: cover;
  background:
    linear-gradient(135deg, rgba(51, 99, 165, 0.18), rgba(255, 255, 255, 0.02)),
    linear-gradient(180deg, #8796ab 0%, #62758d 100%);
}

.about-advantage-card__body {
  padding: 18px 18px 18px 20px;
}

.about-advantage-card__title {
  position: relative;
  margin: 0;
  padding-inline-start: 14px;
  font-size: 16px;
  font-weight: 500;
  line-height: 26px;
  color: #2e3441;
}

.about-advantage-card__title::before {
  content: '';
  position: absolute;
  inset-inline-start: 0;
  top: 4px;
  width: 3px;
  height: 18px;
  border-radius: 999px;
  background: #2349a5;
}

.about-advantage-card__description {
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 22px;
  color: #687385;
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .about-shell {
    width: calc(100% - 48px);
    min-width: 752px;
  }

  .about-hero__content {
    padding-left: 0;
  }

  .about-intro__body {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .about-stats {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 16px;
  }

  .about-advantages__grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .about-advantage-card:nth-child(4),
  .about-advantage-card:nth-child(5) {
    grid-column: auto;
  }
}

@media screen and (max-width: 799px) {
  .about-page {
    min-width: 800px;
  }

  .about-shell {
    width: calc(800px - 48px);
  }

  .about-hero__content {
    padding-left: 0;
  }

  .about-hero__title {
    font-size: 60px;
    line-height: 72px;
  }

  .about-section-heading__backdrop {
    font-size: 48px;
  }

  .about-intro__body {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .about-stats {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
  }

  .about-stat-item__value {
    font-size: 28px;
  }

  .about-advantages__grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .about-advantage-card:nth-child(4),
  .about-advantage-card:nth-child(5) {
    grid-column: auto;
  }
}
</style>
